import React from 'react';
import { Redirect, type RouteComponentProps } from 'react-router-dom';
import CoursesIcon from '../icons/courses.svg?react';
import OrgsIcon from '../icons/orgs.svg?react';
import AdminIcon from '../icons/admin.svg?react';
import { useApp, withAppContext } from '../helpers/api-helpers';
import { User } from '../App.interfaces';
import lazyWithPreload from './lazy-with-preload';
import type { PreloadContext } from './route-preload';
// SignIn and AcceptInvite stay plain React.lazy on purpose: they aren't routes
// of their own but components rendered inside the unauthenticated one-offs
// (/password/reset/:code, /invite/:token). The invite route has no `preload`;
// the reset route warms the catalog its sign-in leads to.
const AcceptInvite = React.lazy(() => import('../pages/AcceptInvite'));
const SignIn = React.lazy(() => import('../pages/SignIn'));

// Each route's code, loaded through one instance per route: `preload` and the
// component that renders must be the same lazyWithPreload, and routes sharing
// a module (subscribe) share one. Loaders are dynamic imports so every route
// stays its own chunk (scripts/check-route-chunks.mjs).
const CourseCatalog = lazyWithPreload(() => import('../pages/CourseCatalog'));
const CourseVisit = lazyWithPreload(() => import('../pages/course-visit'));
const Profile = lazyWithPreload(() => import('../pages/profile/Profile'));
const OrganizationDashboardV2 = lazyWithPreload(
  () => import('../pages/organizations/v2/OrganizationDashboardV2'),
);
const OrganizationDashboardV3 = lazyWithPreload(
  () => import('../pages/organizations/v3/OrganizationDashboard'),
);
const CourseQuestions = lazyWithPreload(
  () => import('../pages/admin/course-questions/CourseQuestions'),
);
const Admin = lazyWithPreload(() => import('../pages/admin/Admin'));
const GradingRequestPromptViewer = lazyWithPreload(
  () => import('../pages/GradingRequestPromptViewer'),
);
const GradingRequestEditor = lazyWithPreload(
  () => import('../pages/GradingRequestEditor'),
);
const Seats = lazyWithPreload(() => import('@/pages/seats'));
const Addons = lazyWithPreload(() => import('@/pages/addons'));
const Subscribe = lazyWithPreload(() => import('@/pages/subscribe/subscribe'));
const SharedTopic = lazyWithPreload(() => import('@/pages/share/shared-topic'));
const ProductCoursesPreview = lazyWithPreload(
  () => import('../pages/ProductCoursesPreview'),
);

// Once the catalog has rendered, the likeliest next click is into a course.
// Warm that route's code in the background so the click renders it at once.
// The catalog's own chunk never imports it, so its critical path doesn't grow.
function CatalogWithCourseWarmup(
  props: React.ComponentProps<typeof CourseCatalog>,
) {
  React.useEffect(() => whenIdle(() => void CourseVisit.preload()), []);
  return <CourseCatalog {...props} />;
}

// The timeout keeps a page that never goes idle from deferring the warm-up
// forever. Safari has no requestIdleCallback; the same delay stands in for it,
// long enough that the catalog's own first paint work isn't competing.
const WARMUP_MAX_DELAY_MS = 2000;
const WARMUP_FALLBACK_DELAY_MS = 200;

function whenIdle(task: () => void) {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(task, {
      timeout: WARMUP_MAX_DELAY_MS,
    });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(task, WARMUP_FALLBACK_DELAY_MS);
  return () => window.clearTimeout(handle);
}

// Code needs no user; the first query starts as soon as there is one.
async function preloadCatalog({ apiClient, userId }: PreloadContext) {
  const { preloadCatalogData } = await CourseCatalog.load();
  if (apiClient && userId) {
    await preloadCatalogData(apiClient, userId);
  }
}

function Logout() {
  const { signOut, authState } = useApp() as {
    signOut: () => void;
    authState: string;
  };

  React.useEffect(() => {
    if (authState === 'signedIn') {
      signOut();
    }
  }, [signOut, authState]);

  return authState === 'signIn' ? <Redirect to="/" /> : null;
}

export default [
  // Must stay first: page-routes.tsx redirects unknown paths to pages[0].
  {
    title: 'Courses',
    path: '/courses',
    // The catalog and a course visit are separate routes, each its own
    // chunk, so the catalog never waits on course-visit code.
    // scripts/check-route-chunks.mjs keeps it that way.
    exact: true,
    icon: <CoursesIcon />,
    requireAuth: true,
    showTab: true,
    component: CatalogWithCourseWarmup,
    preload: preloadCatalog,
  },
  {
    path: '/courses/:course',
    requireAuth: true,
    showTab: false,
    component: CourseVisit,
    preload: CourseVisit.preload,
  },
  {
    title: 'Profile',
    path: '/profile',
    requireAuth: true,
    showTab: false,
    component: Profile,
    preload: Profile.preload,
  },
  {
    title: 'Organization',
    path: '/organizations/v3',
    icon: <OrgsIcon />,
    requireAuth: true,
    showTab: ({ user }: { user: User }) =>
      user &&
      user.memberships &&
      user.memberships.filter(
        (m) => m.role && ['admin', 'observer'].includes(m.role),
      ).length > 0,
    component: OrganizationDashboardV3,
    preload: OrganizationDashboardV3.preload,
  },
  {
    title: 'Organization',
    path: '/organizations',
    requireAuth: true,
    showTab: false,
    component: OrganizationDashboardV2,
    preload: OrganizationDashboardV2.preload,
  },
  {
    path: '/admin/courses/:courseId/questions',
    requireAuth: true,
    showTab: false,
    authorizedGroups: ['admin'],
    component: CourseQuestions,
    preload: CourseQuestions.preload,
  },
  {
    // The path is a contract: the study guide editor's preview link in
    // ui-admin hardcodes it. It sits under /admin like every admin-gated route.
    path: '/admin/courses/:courseId/completion-preview',
    requireAuth: true,
    showTab: false,
    authorizedGroups: ['admin'],
    component: React.lazy(
      () => import('../pages/admin/completion-preview/CompletionPreview'),
    ),
  },
  {
    title: 'Admin',
    path: '/admin',
    icon: <AdminIcon />,
    requireAuth: true,
    showTab: ({ user }: { user: User }) =>
      user && user.groups && user.groups.includes('admin'),
    authorizedGroups: ['admin'],
    component: Admin,
    preload: Admin.preload,
  },
  {
    path: '/grading-requests/:gradingRequestId/prompt',
    requireAuth: true,
    component: GradingRequestPromptViewer,
    preload: GradingRequestPromptViewer.preload,
  },
  {
    path: '/grading-requests/:gradingRequestId',
    requireAuth: true,
    component: GradingRequestEditor,
    preload: GradingRequestEditor.preload,
  },
  {
    path: '/seats/:product',
    requireAuth: true,
    showTab: false,
    component: Seats,
    preload: Seats.preload,
  },
  {
    path: '/addons/:product/:addon',
    requireAuth: true,
    showTab: false,
    component: Addons,
    preload: Addons.preload,
  },
  {
    path: '/subscribe/:product/:key',
    requireAuth: false,
    showTab: false,
    component: Subscribe,
    preload: Subscribe.preload,
  },
  {
    path: '/subscribe/:product',
    requireAuth: false,
    showTab: false,
    component: Subscribe,
    preload: Subscribe.preload,
  },
  {
    path: '/password/reset/:code',
    requireAuth: false,
    showTab: false,
    // Signing in here lands on the catalog, so that is what to warm.
    preload: preloadCatalog,
    component: withAppContext(
      ({
        app: { user, authState },
        history,
        match: {
          params: { code },
        },
      }: {
        app: { user: User; authState: string };
        history: RouteComponentProps['history'];
        match: { params: { code: string } };
      }) => {
        if (authState === 'loading') {
          return null;
        }

        if (user) {
          return <Redirect to="/" />;
        }

        return (
          <SignIn
            defaultParams={{ emailCode: code }}
            onSignIn={() => history.replace('/')}
          />
        );
      },
    ),
  },
  {
    path: '/invite/:token',
    requireAuth: false,
    showTab: false,
    component: (props: RouteComponentProps) => (
      <AcceptInvite token={(props.match.params as { token: string }).token} />
    ),
  },
  {
    path: '/share/topics/:courseID/:conceptID/:topicID',
    requireAuth: false,
    showTab: false,
    component: SharedTopic,
    preload: SharedTopic.preload,
  },
  {
    path: '/logout',
    requireAuth: false,
    component: Logout,
  },
  {
    path: '/preview/:productId/courses',
    requireAuth: false,
    showTab: false,
    component: ProductCoursesPreview,
    preload: ProductCoursesPreview.preload,
  },
  {
    path: '/preview/:product/:course',
    requireAuth: false,
    showTab: false,
    component: ({
      match: {
        params: { product, course },
      },
    }: {
      match: {
        params: {
          product: string;
          course: string;
        };
      };
    }) => (
      <Redirect
        to={`/preview/${product}/courses/${course}/tests/${course}-diagnostic-0`}
      />
    ),
  },
];
